import React, { useState } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

function FileImport({ onImportSuccess }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [importStatus, setImportStatus] = useState(null);

  const processFile = async (file) => {
    setIsProcessing(true);
    setImportStatus(null);
    
    try {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      let users = [];
      
      if (fileExtension === 'csv') {
        // Procesar CSV
        const text = await file.text();
        const result = Papa.parse(text, {
          header: true,
          skipEmptyLines: true,
          transformHeader: (header) => header.trim().toLowerCase()
        });
        
        users = result.data.map(row => ({
          name: row.owner || row.name || '',
          group: row.groupname || row.group || '',
          employeeNumber: row.employeecode || row.employeenumber || '',
          location: row.wardrobename || row.location || '',
          lockerNumber: row.locker || row.lockernumber || ''
        })).filter(user => user.name.trim() !== '');
      } else if (fileExtension === 'xlsx' || fileExtension === 'xls') {
        // Procesar Excel
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        users = jsonData.map(row => ({
          name: row.Owner || row.owner || row.Name || row.name || '',
          group: row.GroupName || row.groupname || row.Group || row.group || '',
          employeeNumber: row.EmployeeCode || row.employeecode || row.EmployeeNumber || row.employeenumber || '',
          location: row.WardrobeName || row.wardrobename || row.Location || row.location || '',
          lockerNumber: row.Locker || row.locker || row.LockerNumber || row.lockernumber || ''
        })).filter(user => user.name.trim() !== '');
      } else {
        throw new Error('Formato de archivo no soportado. Use CSV o Excel (.xlsx, .xls)');
      }
      
      if (users.length === 0) {
        throw new Error('No se encontraron usuarios válidos en el archivo');
      }
      
      // Guardar en localStorage
      localStorage.setItem('uniformApp_users_from_file', JSON.stringify({
        data: users,
        importDate: new Date().toISOString(),
        fileName: file.name
      }));
      
      setImportStatus({
        type: 'success',
        message: `Se importaron ${users.length} usuarios correctamente`
      });
      
      if (onImportSuccess) {
        onImportSuccess(users);
      }
    } catch (error) {
      setImportStatus({
        type: 'error',
        message: `Error al procesar el archivo: ${error.message}`
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      processFile(file);
    }
  };

  const clearImportedData = () => {
    localStorage.removeItem('uniformApp_users_from_file');
    setImportStatus({
      type: 'info',
      message: 'Datos importados eliminados correctamente'
    });
    if (onImportSuccess) {
      onImportSuccess([]);
    }
  };

  const checkImportedData = () => {
    const importedData = localStorage.getItem('uniformApp_users_from_file');
    if (importedData) {
      const data = JSON.parse(importedData);
      const importDate = new Date(data.importDate).toLocaleDateString('es-ES');
      setImportStatus({
        type: 'info',
        message: `Ya hay ${data.data.length} usuarios importados desde ${data.fileName} el ${importDate}`
      });
    }
  };

  React.useEffect(() => {
    checkImportedData();
  }, []);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Importar Usuarios desde Archivo</h3>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-blue-800 mb-2">
            <strong>Formato del archivo:</strong>
          </p>
          <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
            <li>El archivo debe tener columnas: "Owner" (o "Name"), "GroupName" (o "Group")</li>
            <li>Otros campos opcionales: "EmployeeCode", "WardrobeName", "Locker"</li>
            <li>Formatos soportados: CSV (.csv) y Excel (.xlsx, .xls)</li>
          </ul>
        </div>
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <label className="flex-1">
              <input 
                type="file" 
                accept=".csv,.xlsx,.xls" 
                onChange={handleFileChange} 
                disabled={isProcessing}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-full file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100
                  file:cursor-pointer
                  disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </label>
          </div>
          
          {isProcessing && (
            <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span className="text-sm text-gray-600">Procesando archivo...</span>
              </div>
            </div>
          )}
          
          {importStatus && (
            <div className={`p-3 rounded-lg ${
              importStatus.type === 'success' ? 'bg-green-50 border border-green-200' : 
              importStatus.type === 'error' ? 'bg-red-50 border border-red-200' : 
              'bg-blue-50 border border-blue-200'
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start">
                  <span className={`text-sm ${
                    importStatus.type === 'success' ? 'text-green-800' : 
                    importStatus.type === 'error' ? 'text-red-800' : 
                    'text-blue-800'
                  }`}>
                    {importStatus.message}
                  </span>
                </div>
                {importStatus.type !== 'info' && (
                  <button 
                    onClick={() => setImportStatus(null)}
                    className="ml-2 text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          )}
          
          {localStorage.getItem('uniformApp_users_from_file') && (
            <div className="mt-4">
              <button 
                onClick={clearImportedData}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-sm"
              >
                Eliminar Datos Importados
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default FileImport;